import { Controller } from "@hotwired/stimulus"

const NEUTRAL = new Set(["", "neutra", "neutral"])
const IDENTITY_KINDS = new Set(["hero", "background", "subclass"])

function foldSearch(str) {
  return String(str || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLocaleLowerCase("en-US")
}

function normalizeTag(tag) {
  return String(tag || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase()
}

function requirementGroups(card) {
  const raw = String(card.access_tags || "").trim()
  if (raw) {
    return raw
      .split(/\s*•\s*/)
      .map((orPart) =>
        orPart
          .split(/\s+e\s+/i)
          .map((p) => normalizeTag(p))
          .filter(Boolean)
      )
      .filter((g) => g.length > 0)
  }
  const className = String(card.class_name || "").trim()
  if (NEUTRAL.has(className.toLowerCase())) return []
  if (!className) return []
  return [[normalizeTag(`Classe: ${className}`)]]
}

function isNeutral(card) {
  return requirementGroups(card).length === 0
}

function sameClass(left, right) {
  if (!left || !right) return false
  const a = String(left.class_name || "").trim()
  const b = String(right.class_name || "").trim()
  if (!a || !b) return false
  if (NEUTRAL.has(a.toLowerCase()) || NEUTRAL.has(b.toLowerCase())) return true
  return a.toLowerCase() === b.toLowerCase()
}

function activeTagsForIdentity(hero, background, subclass) {
  const tags = []
  if (hero) {
    const className = String(hero.class_name || "").trim()
    if (className && !NEUTRAL.has(className.toLowerCase())) {
      tags.push(normalizeTag(`Classe: ${className}`))
    }
  }
  if (background) {
    const name = background.display_name || background.card_id
    if (name) tags.push(normalizeTag(`Background: ${name}`))
  }
  if (subclass) {
    const name = subclass.display_name || subclass.card_id
    if (name) tags.push(normalizeTag(`Subclasse: ${name}`))
  }
  return tags
}

function usable(card, activeTags) {
  const groups = requirementGroups(card)
  if (groups.length === 0) return true
  const active = new Set(activeTags)
  return groups.some((ands) => ands.every((t) => active.has(t)))
}

export default class extends Controller {
  static targets = [
    "search",
    "kindFilter",
    "catalogList",
    "deckList",
    "heroSlot",
    "backgroundSlot",
    "subclassSlot",
    "heroInput",
    "backgroundInput",
    "subclassInput",
    "cardsJson",
    "badges",
    "filterHint",
    "modeHero",
    "modeBackground",
    "modeSubclass",
    "modeMain",
    "nameInput",
    "form",
    "preview",
    "previewImage",
    "previewCaption"
  ]

  static values = { initial: Object }

  connect() {
    const initial = this.initialValue || {}
    this.catalog = initial.catalog || []
    this.mainKinds = new Set(initial.main_kinds || [])
    this.byId = Object.fromEntries(this.catalog.map((c) => [c.card_id, c]))
    this.heroCardId = initial.hero_card_id || null
    this.backgroundCardId = initial.background_card_id || null
    this.subclassCardId = initial.subclass_card_id || null
    this.counts = {}
    ;(initial.cards || []).forEach((row) => {
      this.counts[row.card_id] = row.count
    })
    this.mode = this.heroCardId ? "main" : "hero"
    this.syncModeButtons()
    this.renderAll()
    this.savedSnapshot = this.snapshot()
    this._onBeforeUnload = (event) => {
      if (!this.isDirty()) return
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", this._onBeforeUnload)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this._onBeforeUnload)
    this.hidePreview()
  }

  previewActions() {
    return "mouseenter->precon-builder#showPreview mouseleave->precon-builder#hidePreview mousemove->precon-builder#movePreview"
  }

  showPreview(event) {
    if (!this.hasPreviewTarget || !this.hasPreviewImageTarget) return
    const cardId = event.currentTarget.dataset.cardId
    const card = this.byId[cardId]
    const src = card?.preview_url || card?.thumb_url
    if (!src) {
      this.hidePreview()
      return
    }
    this.previewImageTarget.src = src
    this.previewImageTarget.alt = card.display_name || cardId
    if (this.hasPreviewCaptionTarget) {
      this.previewCaptionTarget.textContent = card.display_name || cardId
    }
    this.previewTarget.hidden = false
    this.previewTarget.setAttribute("aria-hidden", "false")
    this.movePreview(event)
  }

  movePreview(event) {
    if (!this.hasPreviewTarget || this.previewTarget.hidden) return
    const pad = 16
    const gap = 20
    const rect = this.previewTarget.getBoundingClientRect()
    const width = rect.width || 252
    const height = rect.height || 370
    let left = event.clientX + gap
    let top = event.clientY + gap
    if (left + width + pad > window.innerWidth) {
      left = event.clientX - width - gap
    }
    if (top + height + pad > window.innerHeight) {
      top = window.innerHeight - height - pad
    }
    left = Math.max(pad, left)
    top = Math.max(pad, top)
    this.previewTarget.style.left = `${left}px`
    this.previewTarget.style.top = `${top}px`
  }

  hidePreview() {
    if (!this.hasPreviewTarget) return
    this.previewTarget.hidden = true
    this.previewTarget.setAttribute("aria-hidden", "true")
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.removeAttribute("src")
      this.previewImageTarget.alt = ""
    }
    if (this.hasPreviewCaptionTarget) this.previewCaptionTarget.textContent = ""
  }

  snapshot() {
    const cards = Object.entries(this.counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([card_id, count]) => `${card_id}:${count}`)
      .join(",")
    return JSON.stringify({
      hero: this.heroCardId || "",
      background: this.backgroundCardId || "",
      subclass: this.subclassCardId || "",
      name: this.nameValue(),
      cards
    })
  }

  nameValue() {
    if (this.hasNameInputTarget) return this.nameInputTarget.value.trim()
    return ""
  }

  isDirty() {
    return this.snapshot() !== this.savedSnapshot
  }

  submitSave() {
    this.persist()
    const form = this.hasFormTarget ? this.formTarget : this.element.querySelector("form")
    if (!form) return
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  confirmLeave(event) {
    if (!this.isDirty()) return
    const saveFirst = window.confirm(
      "You have unsaved deck changes. Save them before leaving?\n\nOK = Save now\nCancel = Leave without saving"
    )
    if (saveFirst) {
      event.preventDefault()
      this.submitSave()
    }
  }

  confirmDestructive(event) {
    if (this.isDirty()) {
      if (!window.confirm("You have unsaved deck changes. Delete this precon and discard them?")) {
        event.preventDefault()
        event.stopImmediatePropagation()
      }
      return
    }
    if (!window.confirm("Delete this precon?")) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  markSaved() {
    this.savedSnapshot = this.snapshot()
  }

  setMode(event) {
    event?.preventDefault()
    const mode = event.currentTarget.dataset.mode
    if (!mode) return
    this.mode = mode
    this.syncModeButtons()
    this.renderCatalog()
  }

  setHeroMode(event) {
    event?.preventDefault()
    this.mode = "hero"
    this.syncModeButtons()
    this.renderCatalog()
  }

  setBackgroundMode(event) {
    event?.preventDefault()
    this.mode = "background"
    this.syncModeButtons()
    this.renderCatalog()
  }

  setSubclassMode(event) {
    event?.preventDefault()
    this.mode = "subclass"
    this.syncModeButtons()
    this.renderCatalog()
  }

  setMainMode(event) {
    event?.preventDefault()
    this.mode = "main"
    this.syncModeButtons()
    this.renderCatalog()
  }

  syncModeButtons() {
    const modes = [
      ["modeHero", "hero"],
      ["modeBackground", "background"],
      ["modeSubclass", "subclass"],
      ["modeMain", "main"]
    ]
    modes.forEach(([targetName, mode]) => {
      const has = `has${targetName[0].toUpperCase()}${targetName.slice(1)}Target`
      if (!this[has]) return
      const el = this[`${targetName}Target`]
      const active = this.mode === mode
      el.classList.toggle("cp-btn-primary", active)
      el.classList.toggle("cp-btn-secondary", !active)
    })
  }

  clearMain(event) {
    event?.preventDefault()
    this.counts = {}
    this.renderAll()
  }

  renderAll() {
    this.persist()
    this.renderIdentitySlot("hero")
    this.renderIdentitySlot("background")
    this.renderIdentitySlot("subclass")
    this.renderDeck()
    this.renderBadges()
    this.populateKindFilter()
    this.renderCatalog()
  }

  hero() {
    return this.heroCardId ? this.byId[this.heroCardId] : null
  }

  background() {
    return this.backgroundCardId ? this.byId[this.backgroundCardId] : null
  }

  subclass() {
    return this.subclassCardId ? this.byId[this.subclassCardId] : null
  }

  activeTags() {
    return activeTagsForIdentity(this.hero(), this.background(), this.subclass())
  }

  visibleCatalogCards() {
    const q = foldSearch(this.hasSearchTarget ? this.searchTarget.value : "").trim()
    const kindChip = this.hasKindFilterTarget ? this.kindFilterTarget.value : ""
    const hero = this.hero()
    const active = this.activeTags()

    return this.catalog.filter((card) => {
      if (IDENTITY_KINDS.has(this.mode)) {
        if (card.card_kind !== this.mode) return false
        if (this.mode !== "hero" && hero && !sameClass(hero, card)) return false
      } else {
        if (!this.mainKinds.has(card.card_kind)) return false
        if (hero) {
          if (!usable(card, active)) return false
        } else if (!isNeutral(card)) {
          return false
        }
      }
      if (kindChip && card.card_kind !== kindChip) return false
      if (q) {
        const hay = foldSearch(
          `${card.display_name} ${card.card_id} ${card.class_name || ""} ${card.access_tags || ""} ${card.card_kind || ""}`
        )
        if (!hay.includes(q)) return false
      }
      return true
    })
  }

  populateKindFilter() {
    if (!this.hasKindFilterTarget) return
    const current = this.kindFilterTarget.value
    const kinds = new Set()
    const sample = this.catalog.filter((card) => {
      if (IDENTITY_KINDS.has(this.mode)) return card.card_kind === this.mode
      return this.mainKinds.has(card.card_kind)
    })
    sample.forEach((c) => kinds.add(c.card_kind))
    const opts = ['<option value="">All visible</option>']
    ;[...kinds].sort().forEach((k) => {
      opts.push(`<option value="${k}" ${k === current ? "selected" : ""}>${k}</option>`)
    })
    this.kindFilterTarget.innerHTML = opts.join("")
  }

  renderCatalog() {
    if (!this.hasCatalogListTarget) return
    const cards = this.visibleCatalogCards()
    if (this.hasFilterHintTarget) {
      if (this.mode === "hero") {
        this.filterHintTarget.textContent = "Showing heroes only."
      } else if (this.mode === "background") {
        this.filterHintTarget.textContent = this.hero()
          ? `Showing backgrounds for ${this.hero().class_name}.`
          : "Showing all backgrounds. Pick a hero to filter by class."
      } else if (this.mode === "subclass") {
        this.filterHintTarget.textContent = this.hero()
          ? `Showing subclasses for ${this.hero().class_name}.`
          : "Showing all subclasses. Pick a hero to filter by class."
      } else if (this.hero()) {
        this.filterHintTarget.textContent = `Showing cards usable with current identity (${this.hero().class_name}). Invalid kinds/access omitted.`
      } else {
        this.filterHintTarget.textContent =
          "No hero selected — showing neutral main-deck cards only. Pick a hero to unlock class cards."
      }
    }

    if (cards.length === 0) {
      this.catalogListTarget.innerHTML = `<p class="text-sm text-paper-muted p-2">No cards match.</p>`
      return
    }

    this.catalogListTarget.innerHTML = cards
      .map((card) => {
        const count = this.counts[card.card_id] || 0
        const maxed = this.mode === "main" && count >= 4
        const thumb = card.thumb_url
          ? `<img src="${card.thumb_url}" alt="" class="precon-builder__thumb" width="40" height="56">`
          : `<div class="precon-builder__thumb precon-builder__thumb--empty"></div>`
        const classes = [
          "precon-builder__catalog-row",
          maxed ? "precon-builder__catalog-row--maxed" : ""
        ]
          .filter(Boolean)
          .join(" ")
        return `<button type="button" class="${classes}" data-card-id="${card.card_id}" ${maxed ? "aria-disabled=\"true\"" : ""} data-action="precon-builder#pickCard ${this.previewActions()}">
          ${thumb}
          <span class="precon-builder__catalog-meta">
            <span class="font-medium">${escapeHtml(card.display_name)}</span>
            <span class="text-xs text-paper-muted">${escapeHtml(card.card_id)} · ${escapeHtml(card.card_kind)}${card.class_name ? ` · ${escapeHtml(card.class_name)}` : ""}</span>
          </span>
          ${this.mode === "main" && count ? `<span class="cp-badge cp-badge-muted">${count}/4</span>` : ""}
        </button>`
      })
      .join("")
  }

  pickCard(event) {
    const btn = event.currentTarget
    const cardId = btn.dataset.cardId
    const card = this.byId[cardId]
    if (!card) return

    if (this.mode === "hero") {
      if (card.card_kind !== "hero") return
      const prev = this.heroCardId
      this.heroCardId = cardId
      if (prev !== cardId) this.stripIncompatible()
      this.mode = this.backgroundCardId ? (this.subclassCardId ? "main" : "subclass") : "background"
      this.syncModeButtons()
      this.renderAll()
      return
    }

    if (this.mode === "background") {
      if (card.card_kind !== "background") return
      const hero = this.hero()
      if (hero && !sameClass(hero, card)) return
      this.backgroundCardId = cardId
      this.stripIncompatible()
      this.mode = this.subclassCardId ? "main" : "subclass"
      this.syncModeButtons()
      this.renderAll()
      return
    }

    if (this.mode === "subclass") {
      if (card.card_kind !== "subclass") return
      const hero = this.hero()
      if (hero && !sameClass(hero, card)) return
      this.subclassCardId = cardId
      this.stripIncompatible()
      this.mode = "main"
      this.syncModeButtons()
      this.renderAll()
      return
    }

    if (!this.mainKinds.has(card.card_kind)) return
    const hero = this.hero()
    const active = this.activeTags()
    if (hero && !usable(card, active)) return
    if (!hero && !isNeutral(card)) return

    const next = (this.counts[cardId] || 0) + 1
    if (next > 4) return
    this.counts[cardId] = next
    this.renderAll()
  }

  stripIncompatible() {
    const hero = this.hero()
    if (this.backgroundCardId) {
      const bg = this.background()
      if (!bg || (hero && !sameClass(hero, bg))) this.backgroundCardId = null
    }
    if (this.subclassCardId) {
      const sc = this.subclass()
      if (!sc || (hero && !sameClass(hero, sc))) this.subclassCardId = null
    }

    const active = this.activeTags()
    Object.keys(this.counts).forEach((cid) => {
      const card = this.byId[cid]
      if (!card) {
        delete this.counts[cid]
        return
      }
      if (!this.mainKinds.has(card.card_kind) || (hero && !usable(card, active))) {
        delete this.counts[cid]
      }
    })
  }

  adjustCount(event) {
    const btn = event.currentTarget
    const cardId = btn.dataset.cardId
    const delta = Number(btn.dataset.delta)
    const next = (this.counts[cardId] || 0) + delta
    if (next <= 0) {
      delete this.counts[cardId]
    } else if (next > 4) {
      return
    } else {
      this.counts[cardId] = next
    }
    this.renderAll()
  }

  removeCard(event) {
    const cardId = event.currentTarget.dataset.cardId
    delete this.counts[cardId]
    this.renderAll()
  }

  renderIdentitySlot(kind) {
    const slotName = `${kind}Slot`
    const hasSlot = `has${kind[0].toUpperCase()}${kind.slice(1)}SlotTarget`
    if (!this[hasSlot]) return
    const slot = this[`${slotName}Target`]
    const cardId = this[`${kind}CardId`]
    const card = cardId ? this.byId[cardId] : null
    const labels = {
      hero: { empty: "Click “Pick hero”, then choose a hero from the catalog.", clear: "Clear hero", clearAction: "clearHero" },
      background: {
        empty: "Click “Pick background”, then choose a background from the catalog.",
        clear: "Clear background",
        clearAction: "clearBackground"
      },
      subclass: {
        empty: "Click “Pick subclass”, then choose a subclass from the catalog.",
        clear: "Clear subclass",
        clearAction: "clearSubclass"
      }
    }
    const meta = labels[kind]
    if (!card) {
      slot.innerHTML = `<p class="text-paper-muted text-sm">${meta.empty}</p>`
      return
    }
    const thumb = card.thumb_url
      ? `<img src="${card.thumb_url}" alt="" class="precon-builder__hero-art">`
      : ""
    slot.innerHTML = `
      <div class="precon-builder__hero-chosen" data-card-id="${escapeHtml(card.card_id)}" data-action="${this.previewActions()}">
        ${thumb}
        <div>
          <p class="font-display text-lg">${escapeHtml(card.display_name)}</p>
          <p class="text-sm text-paper-muted">${escapeHtml(card.card_id)} · ${escapeHtml(card.class_name || "")}</p>
          <button type="button" class="cp-btn cp-btn-ghost text-sm mt-2" data-action="precon-builder#${meta.clearAction}">${meta.clear}</button>
        </div>
      </div>`
  }

  clearHero(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.heroCardId = null
    this.stripIncompatible()
    this.mode = "hero"
    this.syncModeButtons()
    this.renderAll()
  }

  clearBackground(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.backgroundCardId = null
    this.stripIncompatible()
    this.mode = "background"
    this.syncModeButtons()
    this.renderAll()
  }

  clearSubclass(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.subclassCardId = null
    this.stripIncompatible()
    this.mode = "subclass"
    this.syncModeButtons()
    this.renderAll()
  }

  renderDeck() {
    if (!this.hasDeckListTarget) return
    const ids = Object.keys(this.counts).sort()
    if (ids.length === 0) {
      this.deckListTarget.innerHTML = `<p class="text-sm text-paper-muted">Deck is empty.</p>`
      return
    }
    this.deckListTarget.innerHTML = ids
      .map((cid) => {
        const card = this.byId[cid] || { display_name: cid, card_kind: "?", class_name: "" }
        const count = this.counts[cid]
        const thumb = card.thumb_url
          ? `<img src="${card.thumb_url}" alt="" class="precon-builder__thumb" width="40" height="56">`
          : `<div class="precon-builder__thumb precon-builder__thumb--empty"></div>`
        return `<div class="precon-builder__deck-row" data-card-id="${escapeHtml(cid)}" data-action="${this.previewActions()}">
          ${thumb}
          <div class="precon-builder__catalog-meta">
            <span class="font-medium">${escapeHtml(card.display_name)}</span>
            <span class="text-xs text-paper-muted">${escapeHtml(cid)} · ${escapeHtml(card.card_kind)}</span>
          </div>
          <div class="precon-builder__qty">
            <button type="button" class="cp-btn cp-btn-ghost" data-card-id="${cid}" data-delta="-1" data-action="precon-builder#adjustCount">−</button>
            <span>${count}</span>
            <button type="button" class="cp-btn cp-btn-ghost" data-card-id="${cid}" data-delta="1" data-action="precon-builder#adjustCount" ${count >= 4 ? "disabled" : ""}>+</button>
            <button type="button" class="cp-btn cp-btn-ghost" data-card-id="${cid}" data-action="precon-builder#removeCard">×</button>
          </div>
        </div>`
      })
      .join("")
  }

  renderBadges() {
    if (!this.hasBadgesTarget) return
    const total = Object.values(this.counts).reduce((a, b) => a + b, 0)
    const chips = [`<span class="cp-badge cp-badge-muted">${total}/60</span>`]
    if (total !== 60) chips.push(`<span class="cp-badge cp-badge-warn">Invalid size</span>`)
    if (!this.heroCardId) chips.push(`<span class="cp-badge cp-badge-warn">Missing hero</span>`)
    if (!this.backgroundCardId) chips.push(`<span class="cp-badge cp-badge-warn">Missing background</span>`)
    if (!this.subclassCardId) chips.push(`<span class="cp-badge cp-badge-warn">Missing subclass</span>`)
    if (total === 60 && this.heroCardId && this.backgroundCardId && this.subclassCardId) {
      chips.push(`<span class="cp-badge cp-badge-ok">Legal</span>`)
    }
    this.badgesTarget.innerHTML = chips.join("")
  }

  persist() {
    if (this.hasHeroInputTarget) this.heroInputTarget.value = this.heroCardId || ""
    if (this.hasBackgroundInputTarget) this.backgroundInputTarget.value = this.backgroundCardId || ""
    if (this.hasSubclassInputTarget) this.subclassInputTarget.value = this.subclassCardId || ""
    if (this.hasCardsJsonTarget) {
      const rows = Object.entries(this.counts).map(([card_id, count]) => ({ card_id, count }))
      this.cardsJsonTarget.value = JSON.stringify(rows)
    }
  }
}

function escapeHtml(str) {
  return String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}
